import {MenuMode} from '@app/modules/shared/models/navigation';

/**
 * Which navigation the screen shows, as a pure function of the facts that decide it.
 *
 *   hidden          no signed-in panel page (front routes, logged out)
 *   drawer          a phone, in either mode: off-canvas
 *   rail            side mode, collapsed - or forced on a narrow window
 *   expanded        side mode, open
 *   overlay         a narrow window's rail opened for a moment, floating over the page
 *
 * Top mode on anything wider than a phone shows no sidebar at all.
 */
export type Viewport = 'mobile' | 'narrow' | 'wide';
export type NavPresentation = 'hidden' | 'drawer' | 'rail' | 'expanded' | 'overlay';

export interface LayoutInput {
  authenticated: boolean;
  showNavbar: boolean;
  mode: MenuMode;
  collapsed: boolean;
  viewport: Viewport;
  drawerOpen: boolean;
  /** A narrow window's rail temporarily opened over the page. */
  narrowExpanded: boolean;
}

export interface NavLayoutState {
  visible: boolean;
  /** Top menu in the header. */
  topLayout: boolean;
  presentation: NavPresentation;
  drawerOpen: boolean;
  /** Whether the collapse toggle means anything here. */
  canCollapse: boolean;
  viewport: Viewport;
}

export function computeLayout(input: LayoutInput): NavLayoutState {
  const visible = input.authenticated && input.showNavbar;
  const base = {visible, viewport: input.viewport, canCollapse: false, drawerOpen: false};
  if (!visible) {
    return {...base, topLayout: false, presentation: 'hidden'};
  }
  if (input.viewport === 'mobile') {
    return {...base, topLayout: false, presentation: 'drawer', drawerOpen: input.drawerOpen};
  }
  if (input.mode === 'top') {
    return {...base, topLayout: true, presentation: 'hidden'};
  }
  if (input.viewport === 'narrow') {
    return {...base, topLayout: false, presentation: input.narrowExpanded ? 'overlay' : 'rail', canCollapse: true};
  }
  return {...base, topLayout: false, presentation: input.collapsed ? 'rail' : 'expanded', canCollapse: true};
}

/**
 * Classes for <html>: they set --sn-sidebar-width, which everything fixed to the left edge
 * (footer, operation bar, loading overlay) and the page itself offset by. A floating overlay
 * or a drawer takes no room, so neither changes it.
 */
export function rootClassesFor(state: NavLayoutState): string[] {
  const classes: string[] = [];
  if (state.presentation === 'expanded') {
    classes.push('sn-layout-side', 'sn-sidebar-expanded');
  }
  if (state.presentation === 'rail' || state.presentation === 'overlay') {
    classes.push('sn-layout-side', 'sn-sidebar-rail');
  }
  if (state.drawerOpen) {
    classes.push('sn-drawer-open');
  }
  return classes;
}

/** Every class rootClassesFor can add, so the stale ones can be taken off. */
export const ROOT_CLASSES = ['sn-layout-side', 'sn-sidebar-expanded', 'sn-sidebar-rail', 'sn-drawer-open'];

const RICH_EDITORS = '[contenteditable="true"], .ace_editor, .CodeMirror, iframe';

/**
 * Ctrl/Cmd+K opens menu search - unless a dialog has the page, an input method is
 * composing, or a rich editor has focus: rich-text editors use Ctrl+K for "insert link".
 * AltGr arrives as Ctrl+Alt on Windows and must not count. There is deliberately no
 * single-key shortcut (WCAG 2.1.4).
 */
export function isSearchShortcut(event: KeyboardEvent, modalOpen: boolean): boolean {
  if (!event || event.defaultPrevented || event.isComposing || event.repeat) {
    return false;
  }
  if ((event.key || '').toLowerCase() !== 'k') {
    return false;
  }
  if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) {
    return false;
  }
  if (modalOpen) {
    return false;
  }
  const target = event.target as Element | null;
  if (target && typeof (target as any).closest === 'function' && (target as any).closest(RICH_EDITORS)) {
    return false;
  }
  return true;
}
